import useDispatchAction from '@hooks/useDispatchAction'
import {ModalConnectWithPatient} from 'components/modal/Leads/Overview/ModalConnectWithPatient'
import Page from 'components/page/Page'
import When from 'components/when/When'
import {AuthContext} from 'context/AuthContext'
import {useCallback, useContext, useEffect, useMemo, useRef, useState} from 'react'
import {useSelector} from 'react-redux'
import {useParams, useSearchParams} from 'react-router-dom'
import {getManufacturingListDetails} from 'redux/Slices/AppSlice/LeadsProfile/GettingStartedOverview/GettingStartedOverview.slice'
import {getApiLeadsOverview} from 'redux/Slices/AppSlice/LeadsProfile/LeadsProfile.slice'
import {getNewTreatmentList} from 'redux/Slices/AppSlice/Kanban/Kanban.slice'
import {
  getAllTreatmentPlanList,
  getTreatmentPlan,
} from 'redux/Slices/AppSlice/LeadsProfile/LeadsProfileTreatmentPlan.slice'
import treatmentPlanStatusConstants from '@constants/treatmentPlanStatus.constants'
import {RootState} from 'redux/store'
import InvitePatientInfoCard from 'screens/Patients/LeadsProfile/main/overview/components/InvitePatientInfoCard'
import StartingSoonStep from 'screens/Patients/LeadsProfile/main/overview/components/StartingSoonStep'
import PatientProfileOverview from 'screens/Patients/NewPatientProfile/PatientProfileOverview'
import {ApiGetData, safeParseInt} from 'utils/ConstFunctions'
import PaperPlaneTiltIcon from 'assets/icons/PaperPlaneTiltIcon'
import useAllUserPlan from '@hooks/useAllUserPlan'
import BracesTracking from './BracesTracking'
import {useTreatmentPlanFilter} from '@hooks/useTreatmentPlanFilter'
import TreatmentStartedIntro from 'screens/Patients/LeadsProfile/main/overview/components/TreatmentStartedIntro'
import {AllTreatmentPlanListItem} from 'screens/Patients/LeadsProfile/main/treatment/types/treatmentPlan.types'
import {NewPlanData} from '../types/PlanList.types'

const TREATMENT_PROGRESS_LOADING_KEY = 'treatment_progress_loading'

const parseAlignerSeries = (series?: string | null) => {
  const matches = String(series ?? '').match(/\d+/g) ?? []
  const start = safeParseInt(matches[0])
  const end = safeParseInt(matches[1] ?? matches[0])

  if (!start || !end || end < start) {
    return {
      starts_with: 0,
      ends_with: 0,
      range: [],
    }
  }

  return {
    starts_with: start,
    ends_with: end,
    range: Array.from({length: end - start + 1}, (_, index) => start + index),
  }
}

const getRangeCount = ({starts_with, ends_with}: {starts_with: number; ends_with: number}) => {
  if (!starts_with || !ends_with || ends_with < starts_with) return 0
  return ends_with - starts_with + 1
}

const normalizeWorkflowPlan = (plan: NewPlanData): AllTreatmentPlanListItem => {
  const upperJaw = parseAlignerSeries(plan.upper_aligner_series)
  const lowerJaw = parseAlignerSeries(plan.lower_aligner_series)

  return {
    aligner_treatment_id: safeParseInt(plan.plan_id),
    braces_treatment_created_on: null,
    braces_treatment_id: null,
    total_aligner: getRangeCount(upperJaw) + getRangeCount(lowerJaw),
    treatment_name: plan.treatment_plan_name ?? 'Treatment Plan',
    created_by_me: false,
    planning_link: plan.planning_link ?? '',
    treatment_status: plan.status,
    treatment_type: 'ALIGNERS',
    order_id: plan.order_id,
    created_at: plan.created_date ?? plan.upload_date ?? '',
    treatment_planning_link: plan.planning_link ?? null,
    doctor_added_treatment_name: plan.treatment_plan_name ?? '',
    treatment_plan_tag_name: plan.treatment_plan_tag_name,
    approved_by_patient_at: plan.approved_date,
    approved_by_patient: false,
    braces_notes_attached: false,
    upper_jaw_details: upperJaw,
    lower_jaw_details: lowerJaw,
    initiator_status: plan.initiator_status,
    approver_status: plan.approver_status,
    treatment_plan_metadata: plan.treatment_plan_metadata
      ? {
          replan_reason: plan.treatment_plan_metadata.replan_reason ?? null,
          replan_requested_on: plan.treatment_plan_metadata.replan_requested_on,
        }
      : undefined,
  }
}

const Tracking = () => {
  const {patientId} = useParams()
  const {userId, organizationId} = useContext(AuthContext)
  const {isEnterprisePlanUser, isGrowthPlanUser} = useAllUserPlan()
  const [searchParams, setSearchParams] = useSearchParams()
  const rawOrderIdFromUrl = searchParams.get('order_id')
  const orderIdFromUrl =
    rawOrderIdFromUrl && rawOrderIdFromUrl !== 'null' ? rawOrderIdFromUrl : null
  const treatmentIdFromUrl =
    searchParams.get('treatment_plan_id') ?? searchParams.get('treatmentPlanId')
  const patientType = searchParams.get('patientType') || 'EXISTING_PATIENT'

  const isLoadingData = searchParams.get('loading_data') === 'true'
  const isTreatmentStartedParam = searchParams.get('treatment_started') === 'true'

  const {isPastPlan, isStarterPlanUser} = useTreatmentPlanFilter()
  const {selectedOrderId} = useSelector((state: RootState) => state.customerPatientProfile)
  const {patientOrderList} = useSelector((state: RootState) => state.profile)

  const {dispatchAction} = useDispatchAction()
  const {data} = useSelector((state: RootState) => state.apiGetLeadsProfileDetails)
  const {dataLeadsOverview, loadingLeadsOverview, getPatientTimelineLoading} = useSelector(
    (state: RootState) => state.leadsProfile
  )
  const {allTreatmentPlanList, getAllTreatmentPlanListLoading} = useSelector(
    (state: RootState) => state.leadsProfileTreatmentPlanReducer
  )
  const {plansList, loadingPlansList} = useSelector((state: RootState) => state.kanban)
  const {manufacturingListByPlan, loadingManufacturingListByPlan} = useSelector(
    (state: RootState) => state.GettingStartedOverview
  )
  const {isModalConnectWithPatientOpen} = useSelector(
    (state: RootState) => state.apiAddAndSendInvite
  )

  // State to track if we're waiting for data to load after intro
  const [isWaitingForData, setIsWaitingForData] = useState(isLoadingData)
  const [hasCompletedStartTreatmentTransition, setHasCompletedStartTreatmentTransition] =
    useState(false)
  const [isPendingLoader, setIsPendingLoader] = useState(isLoadingData)

  useEffect(() => {
    if (isLoadingData) {
      setIsWaitingForData(true)
      setIsPendingLoader(true)
      setHasCompletedStartTreatmentTransition(false)
      if (typeof window !== 'undefined') {
        sessionStorage.setItem(TREATMENT_PROGRESS_LOADING_KEY, 'true')
      }
      return
    }

    // A session flag from another or interrupted treatment must not block a normal visit.
    setIsWaitingForData(false)
    setIsPendingLoader(false)
    if (typeof window !== 'undefined') {
      sessionStorage.removeItem(TREATMENT_PROGRESS_LOADING_KEY)
    }
  }, [isLoadingData])

  useEffect(() => {
    setHasCompletedStartTreatmentTransition(false)
  }, [patientId])

  const clearTreatmentProgressLoading = useCallback(() => {
    setIsWaitingForData(false)
    setIsPendingLoader(false)
    setHasCompletedStartTreatmentTransition(true)
    if (typeof window !== 'undefined') {
      sessionStorage.removeItem(TREATMENT_PROGRESS_LOADING_KEY)
    }

    const currentParams = new URLSearchParams(window.location.search)
    let changed = false

    if (currentParams.has('loading_data')) {
      currentParams.delete('loading_data')
      changed = true
    }

    if (currentParams.has('treatment_started')) {
      currentParams.delete('treatment_started')
      changed = true
    }

    if (changed) {
      setSearchParams(currentParams, {replace: true})
    }
  }, [setSearchParams])

  // Safely extract data with fallbacks
  const dataLeadsData = dataLeadsOverview || {}
  const tracking_enabled = dataLeadsData?.tracking?.enabled
  const bracesTrackingEnabled = dataLeadsData?.braces_journey_tracking_response?.enabled
  const bracesJourneyId = dataLeadsData?.braces_journey_tracking_response?.braces_journey_id
  const treatmentPlanId = dataLeadsData?.treatment_plan_id

  // Track the current treatment ID and order ID to detect changes
  const currentTreatmentIdRef = useRef<string | number | null>(treatmentIdFromUrl)
  const currentOrderIdRef = useRef<string | null>(null)

  const latestOrderId = useMemo(() => {
    const orders = (patientOrderList ?? [])
      .filter((order) => Boolean(order.order_id) && order.case_submitted === true)
      .sort(
        (a, b) =>
          new Date(a.order_creation_date ?? '').getTime() -
          new Date(b.order_creation_date ?? '').getTime()
      )
    return orders.length > 0 ? String(orders[orders.length - 1].order_id) : null
  }, [patientOrderList])

  const isLatestOrder = orderIdFromUrl
    ? latestOrderId !== null && orderIdFromUrl === latestOrderId
    : latestOrderId
      ? selectedOrderId === latestOrderId
      : true
  const currentOrderId = orderIdFromUrl ?? selectedOrderId
  const isExistingPatient =
    (data?.patient_details?.patient_type ?? patientType) === 'EXISTING_PATIENT'
  const isGrowthOrderBasedPatient = isGrowthPlanUser && !isExistingPatient
  const selectedOrderIdForGrowth =
    currentOrderId ?? (isGrowthOrderBasedPatient ? latestOrderId : null)
  const isPreviousOrder = orderIdFromUrl !== null && !isLatestOrder
  const shouldUseTreatmentPlanParam = Boolean(
    treatmentIdFromUrl &&
      (isStarterPlanUser ||
        isEnterprisePlanUser ||
        isGrowthPlanUser ||
        patientType === 'EXISTING_PATIENT')
  )

  // Determine if this is view-only mode
  const isViewOnly = useMemo(() => {
    if (isPreviousOrder) {
      return true
    }
    if (isPastPlan) {
      return true
    }
    return false
  }, [isPreviousOrder, isPastPlan])

  // Determine if we should fetch a specific treatment plan
  const shouldFetchSpecificPlan = useMemo(() => {
    return shouldUseTreatmentPlanParam
  }, [shouldUseTreatmentPlanParam])

  const workflowPlans = useMemo(() => {
    if (!isGrowthOrderBasedPatient) return []
    if (!selectedOrderIdForGrowth) return []

    return (plansList?.plans_list ?? [])
      .filter(
        (plan: NewPlanData) => String(plan?.order_id ?? '') === String(selectedOrderIdForGrowth)
      )
      .map(normalizeWorkflowPlan)
      .filter((plan) => plan.aligner_treatment_id > 0)
  }, [isGrowthOrderBasedPatient, plansList?.plans_list, selectedOrderIdForGrowth])

  const treatmentPlanCandidates = useMemo(() => {
    const plans = Array.isArray(allTreatmentPlanList) ? allTreatmentPlanList : []

    if (isGrowthOrderBasedPatient && selectedOrderIdForGrowth) {
      const orderPlans = plans.filter(
        (plan) => String(plan.order_id) === String(selectedOrderIdForGrowth)
      )
      return orderPlans.length ? orderPlans : workflowPlans
    }

    return plans
  }, [allTreatmentPlanList, isGrowthOrderBasedPatient, selectedOrderIdForGrowth, workflowPlans])

  useEffect(() => {
    if (!isGrowthOrderBasedPatient && !isEnterprisePlanUser) return
    if (!patientId || !userId) return
    const orderId = selectedOrderIdForGrowth ?? latestOrderId
    if (!orderId) return

    dispatchAction(
      getNewTreatmentList({
        patient_id: safeParseInt(patientId),
        doctor_id: safeParseInt(userId),
        treatment_subtype: 'ALIGNERS',
        order_id: orderId,
      })
    )
  }, [
    dispatchAction,
    isGrowthOrderBasedPatient,
    isEnterprisePlanUser,
    latestOrderId,
    patientId,
    selectedOrderIdForGrowth,
    userId,
  ])

  // ========== Force refresh data when order_id or loading_data changes ==========
  useEffect(() => {
    if (!patientId || !userId) return
    if (!isLoadingData) return

    const postData: ApiGetData = {
      data: {
        patient_id: safeParseInt(patientId),
        doctor_id: safeParseInt(userId),
      },
    }
    dispatchAction(getApiLeadsOverview(postData))
  }, [dispatchAction, patientId, userId, isLoadingData])

  // ========== Force refresh data when treatment_started param is present ==========
  useEffect(() => {
    if (!isTreatmentStartedParam) return
    if (!patientId || !userId) return

    // Force refresh data when treatment_started is true
    const postData: ApiGetData = {
      data: {
        patient_id: safeParseInt(patientId),
        doctor_id: safeParseInt(userId),
      },
    }

    // Fetch fresh data
    dispatchAction(getApiLeadsOverview(postData))

    if (organizationId) {
      const actionPayload: any = {
        doctor_id: userId!,
        patient_id: patientId!,
        organization_id: safeParseInt(organizationId),
      }
      if (treatmentPlanId) {
        actionPayload.treatment_plan_id = safeParseInt(treatmentPlanId)
      }
      dispatchAction(getAllTreatmentPlanList(actionPayload))
    }

    if (treatmentPlanId) {
      dispatchAction(getTreatmentPlan({aligner_treatment_id: String(treatmentPlanId)}))
    }
  }, [isTreatmentStartedParam, patientId, userId, organizationId, treatmentPlanId, dispatchAction])

  // ========== Prevent showing intro on wrong patient ==========
  useEffect(() => {
    const treatmentStarted = searchParams.get('treatment_started') === 'true'
    if (treatmentStarted && patientId) {
      const storedPatientId = sessionStorage.getItem('treatment_started_patient_id')
      if (storedPatientId && storedPatientId !== patientId) {
        // Different patient - remove the param
        const newParams = new URLSearchParams(searchParams)
        newParams.delete('treatment_started')
        setSearchParams(newParams, {replace: true})
        sessionStorage.removeItem('treatment_started_patient_id')
      }
    }
  }, [searchParams, patientId, setSearchParams])

  useEffect(() => {
    if (shouldFetchSpecificPlan && treatmentIdFromUrl) {
      dispatchAction(getTreatmentPlan({aligner_treatment_id: String(treatmentIdFromUrl)}))
      return
    }

    if (treatmentIdFromUrl && isPastPlan) {
      dispatchAction(getTreatmentPlan({aligner_treatment_id: String(treatmentIdFromUrl)}))
      return
    }

    if (orderIdFromUrl && isPreviousOrder && !treatmentIdFromUrl) {
      return
    }

    if (!treatmentPlanId) return
    dispatchAction(getTreatmentPlan({aligner_treatment_id: String(treatmentPlanId)}))
  }, [
    dispatchAction,
    treatmentPlanId,
    treatmentIdFromUrl,
    isStarterPlanUser,
    isEnterprisePlanUser,
    orderIdFromUrl,
    isPreviousOrder,
    patientType,
    shouldFetchSpecificPlan,
  ])

  useEffect(() => {
    const currentTreatment = treatmentIdFromUrl || treatmentPlanId
    const currentOrder = orderIdFromUrl || selectedOrderId || latestOrderId

    const treatmentChanged = currentTreatmentIdRef.current !== currentTreatment
    const orderChanged = currentOrderIdRef.current !== currentOrder

    if (treatmentChanged || orderChanged) {
      currentTreatmentIdRef.current = currentTreatment
      currentOrderIdRef.current = currentOrder
    }
  }, [treatmentIdFromUrl, treatmentPlanId, orderIdFromUrl, selectedOrderId, latestOrderId])

  const activeTreatmentPlan = useMemo(() => {
    if (!Array.isArray(treatmentPlanCandidates) || treatmentPlanCandidates.length === 0) {
      return null
    }

    if (shouldFetchSpecificPlan && treatmentIdFromUrl) {
      const filtered = treatmentPlanCandidates.filter(
        (plan) => String(plan.aligner_treatment_id) === String(treatmentIdFromUrl)
      )
      if (filtered.length > 0) {
        return filtered[0]
      }
      return null
    }

    const isCurrentOrder =
      isLatestOrder && !isPreviousOrder && !isPastPlan && !shouldFetchSpecificPlan
    if (isCurrentOrder) {
      let matchingPlan = null
      if (treatmentPlanId) {
        matchingPlan = treatmentPlanCandidates.find(
          (plan) => String(plan.aligner_treatment_id) === String(treatmentPlanId)
        )
      }
      if (!matchingPlan) {
        const currentOrderId = orderIdFromUrl || latestOrderId
        if (currentOrderId) {
          matchingPlan = treatmentPlanCandidates.find(
            (plan) => String(plan.order_id) === String(currentOrderId)
          )
        }
      }
      return matchingPlan ?? treatmentPlanCandidates[0] ?? null
    }

    let filteredList = treatmentPlanCandidates

    if (orderIdFromUrl && isPreviousOrder) {
      filteredList = treatmentPlanCandidates.filter(
        (plan) => String(plan.order_id) === String(orderIdFromUrl)
      )
    }

    if (treatmentIdFromUrl && isPastPlan) {
      filteredList = treatmentPlanCandidates.filter(
        (plan) => String(plan.aligner_treatment_id) === String(treatmentIdFromUrl)
      )
    }

    if (filteredList.length === 0) return null

    return (
      filteredList.find((plan) => plan.treatment_status === treatmentPlanStatusConstants.ACTIVE) ??
      filteredList[0]
    )
  }, [
    treatmentPlanCandidates,
    orderIdFromUrl,
    isPreviousOrder,
    treatmentIdFromUrl,
    isStarterPlanUser,
    isPastPlan,
    isLatestOrder,
    treatmentPlanId,
    isEnterprisePlanUser,
    patientType,
    shouldFetchSpecificPlan,
  ])

  const activePlanId = useMemo(() => {
    if (!activeTreatmentPlan?.aligner_treatment_id) {
      return 0
    }
    return safeParseInt(activeTreatmentPlan.aligner_treatment_id)
  }, [activeTreatmentPlan])

  const checkInTreatmentPlan = useMemo(() => {
    const orderId = currentOrderId ?? activeTreatmentPlan?.order_id ?? latestOrderId
    const orderPlan = orderId
      ? plansList?.plans_list?.find(
          (plan: NewPlanData) =>
            safeParseInt(plan.plan_id) === activePlanId && String(plan.order_id) === String(orderId)
        )
      : undefined

    // Use the same complete series as the Plans tab, scoped to the selected plan and order.
    return orderPlan ? normalizeWorkflowPlan(orderPlan) : activeTreatmentPlan
  }, [plansList?.plans_list, activePlanId, currentOrderId, latestOrderId, activeTreatmentPlan])

  useEffect(() => {
    if (!patientId) return
    if (!activePlanId || activePlanId === 0) {
      return
    }

    if (shouldFetchSpecificPlan && treatmentIdFromUrl) {
      dispatchAction(
        getManufacturingListDetails({
          patient_id: safeParseInt(patientId),
          treatment_plan_id: activePlanId,
        })
      )
      return
    }

    const isCurrentOrder =
      isLatestOrder && !isPreviousOrder && !isPastPlan && !shouldFetchSpecificPlan
    if (isCurrentOrder) {
      const treatmentExists = treatmentPlanCandidates.some(
        (plan) => String(plan.aligner_treatment_id) === String(activePlanId)
      )
      if (!treatmentExists) {
        return
      }
    }

    dispatchAction(
      getManufacturingListDetails({
        patient_id: safeParseInt(patientId),
        treatment_plan_id: activePlanId,
      })
    )
  }, [
    patientId,
    activePlanId,
    dispatchAction,
    isLatestOrder,
    isPreviousOrder,
    isStarterPlanUser,
    isPastPlan,
    treatmentPlanCandidates,
    isEnterprisePlanUser,
    patientType,
    treatmentIdFromUrl,
    shouldFetchSpecificPlan,
  ])

  const manufacturingList = activePlanId ? manufacturingListByPlan?.[activePlanId] : undefined

  const hasDeliveredBatch = useMemo(() => {
    if (!manufacturingList?.processed_manufacturing) return false
    const batches = manufacturingList.processed_manufacturing
    if (batches.length === 0) return false
    if (isPreviousOrder) {
      return batches.some((batch) => batch.status === 'DELIVERED')
    }
    if (isPastPlan) {
      return batches.some((batch) => batch.status === 'DELIVERED')
    }
    if (shouldFetchSpecificPlan && treatmentIdFromUrl) {
      return batches.some((batch) => batch.status === 'DELIVERED')
    }
    return batches.some((batch) => batch.status === 'DELIVERED') && isLatestOrder
  }, [
    manufacturingList,
    isPreviousOrder,
    isLatestOrder,
    isPastPlan,
    shouldFetchSpecificPlan,
    treatmentIdFromUrl,
  ])

  const manufacturingLoading = activePlanId ? loadingManufacturingListByPlan?.[activePlanId] : false
  const isStartTreatmentTransitionLoading = isWaitingForData || isLoadingData || isPendingLoader

  const pageLoading =
    loadingLeadsOverview ||
    getPatientTimelineLoading ||
    getAllTreatmentPlanListLoading ||
    (isGrowthOrderBasedPatient && loadingPlansList) ||
    (!!manufacturingLoading && !manufacturingList)

  const shouldShowTracking = useMemo(() => {
    // if (shouldFetchSpecificPlan && treatmentIdFromUrl) {
    //   return manufacturingList?.processed_manufacturing?.length > 0
    // }
    if (isPastPlan) {
      return treatmentIdFromUrl && manufacturingList?.processed_manufacturing?.length > 0
    }
    if (isPreviousOrder) {
      return manufacturingList?.processed_manufacturing?.length > 0
    }
    return tracking_enabled && manufacturingList?.processed_manufacturing?.length > 0
  }, [
    shouldFetchSpecificPlan,
    treatmentIdFromUrl,
    isPastPlan,
    manufacturingList,
    isPreviousOrder,
    tracking_enabled,
  ])

  const shouldRenderTrackingProfile = shouldShowTracking || hasCompletedStartTreatmentTransition

  // Determine the specific treatment ID to pass
  const specificTreatmentIdValue = useMemo(() => {
    if (treatmentIdFromUrl) {
      return treatmentIdFromUrl
    }
    if (activeTreatmentPlan?.aligner_treatment_id) {
      return String(activeTreatmentPlan.aligner_treatment_id)
    }
    return null
  }, [treatmentIdFromUrl, activeTreatmentPlan])

  // ========== EARLY RETURNS ==========

  // Show the intro screen only when treatment was started manually and not during the confirm-start loader transition.
  if (isTreatmentStartedParam && !isLoadingData && !isPendingLoader) {
    return (
      <Page loading={false}>
        <TreatmentStartedIntro />
      </Page>
    )
  }

  // Keep the start-treatment card hidden while the start action and prerequisite data settle.
  if (isStartTreatmentTransitionLoading && !shouldShowTracking) {
    return (
      <Page loading={true} loaderText=''>
        <></>
      </Page>
    )
  }

  // Show empty state for past plans without data
  if (isPastPlan && !treatmentIdFromUrl) {
    return (
      <Page loading={pageLoading}>
        <div className='flex items-center justify-center flex-col gap-4'>
          <div className='rounded-lg bg-white p-6 text-center text-sm text-gray-500'>
            Details will be available when aligners are delivered.
          </div>
        </div>
      </Page>
    )
  }

  // Show empty state for enterprise existing patient without manufacturing data
  if (
    shouldFetchSpecificPlan &&
    treatmentIdFromUrl &&
    !manufacturingList?.processed_manufacturing?.length
  ) {
    return (
      <Page loading={pageLoading}>
        <div className='flex items-center justify-center flex-col gap-4'>
          <div className='rounded-lg bg-white p-6 text-center text-sm text-gray-500'>
            No tracking data available for this treatment plan.
          </div>
        </div>
      </Page>
    )
  }

  // Loading state
  if (pageLoading && !shouldRenderTrackingProfile) {
    return (
      <Page loading={pageLoading}>
        <></>
      </Page>
    )
  }

  // Main render
  return (
    <Page loading={pageLoading && !shouldRenderTrackingProfile}>
      <When isTrue={isModalConnectWithPatientOpen}>
        <ModalConnectWithPatient />
      </When>

      {bracesTrackingEnabled && bracesJourneyId ? (
        <BracesTracking
          patientId={safeParseInt(patientId)}
          bracesJourneyId={bracesJourneyId}
          practiceLocationId={data?.patient_details?.practice_location_id}
        />
      ) : shouldRenderTrackingProfile ? (
        <PatientProfileOverview
          loading={loadingLeadsOverview}
          alignerJourneyId={activeTreatmentPlan?.aligner_treatment_id || null}
          patientId={safeParseInt(patientId)}
          treatmentStatus={
            activeTreatmentPlan?.treatment_status ||
            dataLeadsData?.treatment_plan?.aligner_treatment_status
          }
          isViewOnly={isViewOnly}
          specificTreatmentId={specificTreatmentIdValue}
          treatmentPlan={checkInTreatmentPlan}
          forceLoading={isStartTreatmentTransitionLoading}
          onTimelineLoaded={
            isStartTreatmentTransitionLoading ? clearTreatmentProgressLoading : undefined
          }
        />
      ) : hasDeliveredBatch ? (
        <div className='flex flex-col gap-4 p-4'>
          <StartingSoonStep />
          <When isTrue={!data?.invitation_details?.is_patient_connected && !isEnterprisePlanUser}>
            <InvitePatientInfoCard
              icon={<PaperPlaneTiltIcon color='#be8901' />}
              iconWrapperClassName='flex h-12 w-14 items-center justify-center rounded-xl bg-orangeSupport'
              title='Invite patient'
              description={
                !data?.invitation_details?.is_patient_invited
                  ? "The patient hasn't signed up yet. Send an invitation to start communicating."
                  : "An invite has been sent, but the patient hasn't signed up yet. Resend the invite and ensure they use the same email ID."
              }
              optionalLabel=''
              primaryButtonClassName={
                !data?.invitation_details?.is_patient_invited
                  ? 'rounded-lg px-5 py-3 text-sm font-semibold !h-auto !border-none bg-[#BE8901] text-white transition-opacity duration-200 hover:!opacity-90'
                  : '!bg-lightGray !text-mediumGray cursor-not-allowed'
              }
              cardClassName='flex flex-col md:flex-row md:items-center md:justify-between rounded-xl border border-mediumGray bg-white shadow-sm p-4'
              primaryButtonLabel={
                data?.invitation_details?.is_patient_invited ? 'Resend Invite' : 'Send Invite'
              }
              primaryButtonIcon={null}
            />
          </When>
        </div>
      ) : (
        <div className='flex items-center justify-center flex-col gap-4'>
          <div className='rounded-lg bg-white p-6 text-center text-sm text-gray-500'>
            Details will be available when aligners are delivered.
          </div>
        </div>
      )}
    </Page>
  )
}

export default Tracking
